import { useEffect, useRef, useState } from 'react';
import { useForm, Controller, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Input from '../ui/Input';
import Button from '../ui/Button';
import {
  programSchema,
  PROGRAM_TYPE_LABELS,
  ROYALTY_UNIT_LABELS,
  REPORTING_CADENCE_LABELS,
  type ProgramFormData,
  type CultivarProgram,
  type VisibilityConfig,
} from '../../types/cultivar';
import organizationService, { type OrgSearchResult } from '../../services/organization';
import VisibilityConfigEditor from './VisibilityConfigEditor';
import { Building2, Search, Loader2, X, Award, DollarSign, CalendarClock, FileText, Lock, Globe } from 'lucide-react';


interface ProgramFormProps {
  initialData?: CultivarProgram;
  defaultOrganisationId?: number;
  defaultOrganisationName?: string;
  lockOrganisation?: boolean;
  onSubmit: (data: ProgramFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

function ProgramForm({
  initialData,
  defaultOrganisationId,
  defaultOrganisationName,
  lockOrganisation = false,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: ProgramFormProps) {
  const [orgQuery, setOrgQuery] = useState('');
  const [orgResults, setOrgResults] = useState<OrgSearchResult[]>([]);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [selectedOrgName, setSelectedOrgName] = useState(
    initialData?.organisation_name || defaultOrganisationName || ''
  );
  const orgDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ProgramFormData>({
    resolver: zodResolver(programSchema) as Resolver<ProgramFormData>,
    defaultValues: {
      organisation_id: initialData?.organisation_id ?? defaultOrganisationId ?? 0,
      name: initialData?.name || '',
      program_type: initialData?.program_type || 'annual_tree_royalty',
      custom_type_label: initialData?.custom_type_label || '',
      description: initialData?.description || '',
      active: initialData?.active ?? true,
      royalty_rate:
        initialData?.royalty_rate == null ? null : Number(initialData.royalty_rate),
      royalty_unit: initialData?.royalty_unit || null,
      currency: initialData?.currency || 'AUD',
      minimum_royalty:
        initialData?.minimum_royalty == null ? null : Number(initialData.minimum_royalty),
      reporting_cadence: initialData?.reporting_cadence || null,
      reporting_due_month: initialData?.reporting_due_month ?? null,
      reporting_due_day: initialData?.reporting_due_day ?? null,
      default_terms: initialData?.default_terms || '',
      default_terms_template_id: initialData?.default_terms_template_id ?? null,
      royalty_start_leaf: initialData?.royalty_start_leaf ?? null,
      reporting_start_leaf: initialData?.reporting_start_leaf ?? null,
      default_yield_kg_per_tree_per_year:
        initialData?.default_yield_kg_per_tree_per_year == null ? null
          : Number(initialData.default_yield_kg_per_tree_per_year),
      default_yield_tonnes_per_ha_per_year:
        initialData?.default_yield_tonnes_per_ha_per_year == null ? null
          : Number(initialData.default_yield_tonnes_per_ha_per_year),
      rules: (initialData?.rules as Record<string, unknown>) || {},
      share_with_program_orchards: initialData?.share_with_program_orchards ?? false,
      visibility_config: (initialData?.visibility_config as Record<string, unknown>) || {},
      logo_url: initialData?.logo_url || '',
      hero_image_url: initialData?.hero_image_url || '',
      tagline: initialData?.tagline || '',
      marketing_description: initialData?.marketing_description || '',
      website_terms: initialData?.website_terms || '',
      brand_color: initialData?.brand_color || '',
      external_url: initialData?.external_url || '',
      public: initialData?.public ?? false,
    },
  });

  const programType = watch('program_type');
  const organisationId = watch('organisation_id');
  const showLeafFields =
    programType === 'annual_tree_royalty' || programType === 'production_royalty';

  const searchOrgs = async (query: string) => {
    if (query.length < 2) {
      setOrgResults([]);
      return;
    }
    setLoadingOrgs(true);
    try {
      const response = await organizationService.searchOrganizations({ name: query });
      if (response.success && response.data) setOrgResults(response.data);
    } catch (err) {
      console.error('Failed to search organisations:', err);
    } finally {
      setLoadingOrgs(false);
    }
  };

  // If the caller didn't lock the org and gave no initial/default org, require selection.
  const needsOrgPicker = !lockOrganisation;

  useEffect(() => {
    if (defaultOrganisationId && !initialData?.organisation_id) {
      setValue('organisation_id', defaultOrganisationId);
    }
  }, [defaultOrganisationId, initialData?.organisation_id, setValue]);

  const handleFormSubmit = (data: ProgramFormData) => {
    onSubmit({
      ...data,
      custom_type_label:
        data.program_type === 'other' ? data.custom_type_label || null : null,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Identity */}
      <div className="space-y-4">
        <h3 className="font-semibold text-neutral-900 flex items-center gap-2">
          <Award className="h-5 w-5 text-primary-600" />
          Program identity
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Program Name *"
            {...register('name')}
            error={errors.name?.message}
            placeholder="e.g., Rockit Annual Tree Royalty"
          />

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-neutral-700">
              Program Type *
            </label>
            <select
              {...register('program_type')}
              className={`input-professional w-full ${errors.program_type ? 'border-error-300' : ''}`}
            >
              {Object.entries(PROGRAM_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {errors.program_type && (
              <p className="text-sm text-error-600">{errors.program_type.message}</p>
            )}
          </div>
        </div>

        {programType === 'other' && (
          <Input
            label="Custom Type Label"
            {...register('custom_type_label')}
            error={errors.custom_type_label?.message}
            placeholder="e.g., Per-Pallet Levy"
          />
        )}

        {/* Owning organisation */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-neutral-700">
            Owning Organisation *
          </label>
          {lockOrganisation || (organisationId && selectedOrgName) ? (
            <div className="flex items-center justify-between px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary-600" />
                <span className="text-sm font-medium text-primary-900">
                  {selectedOrgName || 'Selected organisation'}
                </span>
              </div>
              {!lockOrganisation && (
                <button
                  type="button"
                  className="text-primary-500 hover:text-primary-700"
                  onClick={() => {
                    setValue('organisation_id', 0);
                    setSelectedOrgName('');
                    setOrgQuery('');
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : (
            <div className="relative">
              <div className="relative">
                <input
                  type="text"
                  value={orgQuery}
                  onChange={(e) => {
                    const q = e.target.value;
                    setOrgQuery(q);
                    setShowOrgDropdown(true);
                    if (orgDebounceRef.current) clearTimeout(orgDebounceRef.current);
                    orgDebounceRef.current = setTimeout(() => searchOrgs(q), 300);
                  }}
                  onFocus={() => setShowOrgDropdown(true)}
                  onBlur={() => setTimeout(() => setShowOrgDropdown(false), 200)}
                  className="input-professional w-full pl-10"
                  placeholder="Search organisation..."
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
                  {loadingOrgs ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </div>
              </div>
              {showOrgDropdown && orgResults.length > 0 && (
                <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                  {orgResults.map((org) => (
                    <button
                      key={org.id}
                      type="button"
                      className="w-full text-left px-4 py-2.5 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setValue('organisation_id', org.id);
                        setSelectedOrgName(org.name);
                        setOrgQuery('');
                        setShowOrgDropdown(false);
                      }}
                    >
                      <p className="text-sm font-medium text-neutral-900">{org.name}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {errors.organisation_id && (
            <p className="text-sm text-error-600">{errors.organisation_id.message}</p>
          )}
          {needsOrgPicker && (
            <p className="text-xs text-neutral-500">
              The organisation that runs this program (usually the breeder / IP holder).
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-neutral-700">Description</label>
          <textarea
            {...register('description')}
            className="input-professional w-full min-h-[80px]"
            placeholder="What is this program for? Who is eligible to participate?"
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="program_active"
            {...register('active')}
            className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="program_active" className="text-sm text-neutral-700">
            Active (available for assigning to cultivars)
          </label>
        </div>
      </div>

      {/* Royalty structure */}
      <div className="space-y-4 pt-2 border-t border-neutral-200">
        <h3 className="font-semibold text-neutral-900 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-primary-600" />
          Royalty structure
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Controller
            name="royalty_rate"
            control={control}
            render={({ field }) => (
              <Input
                label="Royalty Rate"
                type="number"
                step="0.0001"
                min={0}
                value={field.value == null ? '' : String(field.value)}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                error={errors.royalty_rate?.message as string | undefined}
                placeholder="e.g., 2.50"
              />
            )}
          />

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-neutral-700">Unit</label>
            <select
              {...register('royalty_unit')}
              className="input-professional w-full"
              defaultValue={initialData?.royalty_unit || ''}
            >
              <option value="">— Not set —</option>
              {Object.entries(ROYALTY_UNIT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Currency"
            {...register('currency')}
            error={errors.currency?.message as string | undefined}
            placeholder="AUD"
            maxLength={3}
          />
        </div>

        <Controller
          name="minimum_royalty"
          control={control}
          render={({ field }) => (
            <Input
              label="Minimum Royalty (per reporting period)"
              type="number"
              step="0.01"
              min={0}
              value={field.value == null ? '' : String(field.value)}
              onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
              error={errors.minimum_royalty?.message as string | undefined}
              placeholder="Optional — e.g., 500.00"
            />
          )}
        />
      </div>

      {/* Reporting */}
      <div className="space-y-4 pt-2 border-t border-neutral-200">
        <h3 className="font-semibold text-neutral-900 flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-primary-600" />
          Reporting
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-neutral-700">Cadence</label>
            <select
              {...register('reporting_cadence')}
              className="input-professional w-full"
              defaultValue={initialData?.reporting_cadence || ''}
            >
              <option value="">— Not set —</option>
              {Object.entries(REPORTING_CADENCE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <Controller
            name="reporting_due_month"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-neutral-700">Due Month (1–12)</label>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={field.value == null ? '' : String(field.value)}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                  className="input-professional w-full"
                  placeholder="e.g., 7 = July"
                />
                {errors.reporting_due_month && (
                  <p className="text-xs text-error-600">{errors.reporting_due_month.message as string}</p>
                )}
              </div>
            )}
          />

          <Controller
            name="reporting_due_day"
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-neutral-700">Due Day (1–31)</label>
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={field.value == null ? '' : String(field.value)}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                  className="input-professional w-full"
                  placeholder="e.g., 31"
                />
                {errors.reporting_due_day && (
                  <p className="text-xs text-error-600">{errors.reporting_due_day.message as string}</p>
                )}
              </div>
            )}
          />
        </div>

        {showLeafFields && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-neutral-100">
            <Controller
              name="royalty_start_leaf"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-neutral-700">
                    Royalties begin from leaf #
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={field.value == null ? '' : String(field.value)}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                    className="input-professional w-full"
                    placeholder="e.g., 3 (3rd leaf)"
                  />
                  <p className="text-xs text-neutral-500">
                    Leaf number after planting (1 = planting year). Leave blank for immediate start.
                  </p>
                  {errors.royalty_start_leaf && (
                    <p className="text-xs text-error-600">{errors.royalty_start_leaf.message as string}</p>
                  )}
                </div>
              )}
            />

            <Controller
              name="reporting_start_leaf"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-neutral-700">
                    Reporting begins from leaf #
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={field.value == null ? '' : String(field.value)}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                    className="input-professional w-full"
                    placeholder="same as royalties"
                  />
                  <p className="text-xs text-neutral-500">
                    Leave blank to match royalty start. Set lower if planting disclosures are required earlier.
                  </p>
                  {errors.reporting_start_leaf && (
                    <p className="text-xs text-error-600">{errors.reporting_start_leaf.message as string}</p>
                  )}
                </div>
              )}
            />
          </div>
        )}

        {(programType === 'production_royalty') && (
          <div className="pt-2 border-t border-neutral-100">
            <h4 className="font-semibold text-neutral-700 text-sm mb-1">Default yields (fallback when no production declaration)</h4>
            <p className="text-xs text-neutral-500 mb-3">
              Used to estimate royalties (and generate invoices) when a licensee hasn't uploaded production data for the latest season. Set either column — tree-based wins if both are given.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                name="default_yield_kg_per_tree_per_year"
                control={control}
                render={({ field }) => (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-neutral-700">
                      Default kg per tree per year
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      min={0}
                      value={field.value == null ? '' : String(field.value)}
                      onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                      className="input-professional w-full"
                      placeholder="e.g., 35"
                    />
                  </div>
                )}
              />
              <Controller
                name="default_yield_tonnes_per_ha_per_year"
                control={control}
                render={({ field }) => (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-neutral-700">
                      Default tonnes per hectare per year
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      min={0}
                      value={field.value == null ? '' : String(field.value)}
                      onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                      className="input-professional w-full"
                      placeholder="e.g., 40"
                    />
                  </div>
                )}
              />
            </div>
          </div>
        )}
      </div>

      {/* Default terms */}
      <div className="space-y-4 pt-2 border-t border-neutral-200">
        <h3 className="font-semibold text-neutral-900 flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary-600" />
          Default terms &amp; conditions
        </h3>

        {/* The contracts a programme's orders sign are set per role on its
            Contracts page, by contract rather than by version — so what is
            sent is always the version in force. This form used to pick a
            template row here; that choice was superseded by the roles and is
            no longer offered. */}
        <p className="text-sm text-neutral-600">
          The contracts this programme's orders are signed on are set per role on the programme's
          Contracts page (Programmes → Contracts), and always send the version in force.
        </p>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-neutral-700">
            Supplementary terms (optional)
          </label>
          <textarea
            {...register('default_terms')}
            className="input-professional w-full min-h-[120px] font-mono text-sm"
            placeholder={
              'Additional program-specific clauses appended to (or replacing) the selected template.\n' +
              'Leave blank to use the template as-is. Individual cultivar licences can still have their own terms on top.'
            }
          />
          <p className="text-xs text-neutral-500">
            If no template is selected, this becomes the sole T&amp;C text for the program.
          </p>
        </div>
      </div>

      {/* Public website / marketing */}
      <div className="space-y-4 pt-2 border-t border-neutral-200">
        <h3 className="font-semibold text-neutral-900 flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary-600" />
          Public website
        </h3>
        <p className="text-xs text-neutral-500 -mt-2">
          Shown on the public website panel for any cultivar in this program. Leave fields blank to omit them.
        </p>

        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-0.5" {...register('public')} />
          <span>
            <span className="font-medium">Show this program on the public website.</span>
            <span className="block text-xs text-neutral-500">
              When off, the marketing payload is hidden even for cultivars that are otherwise public.
            </span>
          </span>
        </label>

        <Input
          label="Tagline"
          {...register('tagline')}
          error={errors.tagline?.message as string | undefined}
          placeholder="e.g., A premium late-season blush apple from New Zealand"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Logo URL"
            {...register('logo_url')}
            error={errors.logo_url?.message as string | undefined}
            placeholder="https://…/logo.png"
          />
          <Input
            label="Hero image URL"
            {...register('hero_image_url')}
            error={errors.hero_image_url?.message as string | undefined}
            placeholder="https://…/banner.jpg"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Brand colour (hex)"
            {...register('brand_color')}
            error={errors.brand_color?.message as string | undefined}
            placeholder="#FF6600"
            maxLength={7}
          />
          <Input
            label="External program website"
            {...register('external_url')}
            error={errors.external_url?.message as string | undefined}
            placeholder="https://program.example.com"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-neutral-700">
            Marketing description
          </label>
          <textarea
            {...register('marketing_description')}
            className="input-professional w-full min-h-[100px]"
            placeholder="Long-form public description of the program. Markdown supported."
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-neutral-700">
            Website terms &amp; conditions
          </label>
          <textarea
            {...register('website_terms')}
            className="input-professional w-full min-h-[100px]"
            placeholder="Public conditions of growing or selling cultivars in this program (e.g. licensing notes, geographic restrictions)."
          />
          <p className="text-xs text-neutral-500">
            This is a public summary — it does NOT replace the contractual sublicence terms above.
          </p>
        </div>
      </div>

      {/* Sharing & visibility defaults */}
      <div className="space-y-4">
        <h3 className="font-semibold text-neutral-900 flex items-center gap-2">
          <Lock className="h-5 w-5 text-primary-600" />
          Sharing & visibility defaults
        </h3>

        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-0.5" {...register('share_with_program_orchards')} />
          <span>
            <span className="font-medium">Share cultivars across program orchards.</span>
            <span className="block text-xs text-neutral-500">
              When enabled, an orchard that holds a production licence on any cultivar in this program
              gets the <code>program_orchard</code> view tier on every other cultivar in the program.
            </span>
          </span>
        </label>

        <Controller
          control={control}
          name="visibility_config"
          render={({ field }) => (
            <VisibilityConfigEditor
              value={(field.value as VisibilityConfig) || {}}
              onChange={(next) => field.onChange(next)}
              intro="Default per-audience visibility for cultivars in this program. Individual cultivars can override these settings on their own Visibility tab."
            />
          )}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-neutral-200">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {initialData ? 'Save Changes' : 'Create Program'}
        </Button>
      </div>
    </form>
  );
}

export default ProgramForm;
